//****************************************************************************
//
// Copyright(C) 2025, CyberTAN Technology, Inc. All Rights Reserved.
// This source code contains confidential and proprietary
// information of CyberTAN Technology, Inc. Unauthorized reproduction,
// modification, distribution, or disclosure of this file, in whole or in part,
// by any means, is strictly prohibited without prior written permission from
// CyberTAN Technology, Inc.
//
// THIS SOURCE CODE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
// EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
// MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NONINFRINGEMENT.
//
// IN NO EVENT SHALL CYBERTAN TECHNOLOGY, INC. BE LIABLE FOR ANY CLAIM,
// DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT,
// TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOURCE CODE.
//
//****************************************************************************

import { useCallback, useEffect, useReducer, useRef } from 'react';

export type RetryMode = 'time' | 'count';
export type TaskStatus = 'idle' | 'checking' | 'success' | 'error';

interface UseCheckApiReadyProps {
  checkId: string;
  enableRetry: boolean;
  retryMode: RetryMode;
  checkFunction: () => Promise<{ data: boolean; status: number }>;
  checkResponseData?: (data: any) => boolean;
  callbackFunction: any;
  totalDuration: number; // 時間模式：總執行時間(ms) | 次數模式：總執行次數
  retryInterval: number; // 重試間隔(ms)
  startCheckAt?: number; // 僅在 retryMode = 'time' 時使用
}

interface TaskState {
  status: TaskStatus;
  retryCount: number;
  totalRetryTimeMs: number;
  error?: Error;
}

export interface StartCheckState {
  [key: string]: TaskState;
}

type Action =
  | { type: 'START_CHECK'; payload: { checkId: string } }
  | { type: 'CHECK_SUCCESS'; payload: { checkId: string } }
  | { type: 'CHECK_ERROR'; payload: { checkId: string; error: Error } }
  | { type: 'RETRY'; payload: { checkId: string; retryTime: number } }
  | { type: 'RESET'; payload: { checkId: string } };

const initialTaskState: TaskState = {
  status: 'idle',
  retryCount: 0,
  totalRetryTimeMs: 0,
  error: undefined,
};

const initialState: StartCheckState = {};

function reducer(state: StartCheckState, action: Action): StartCheckState {
  const { checkId } = action.payload;

  switch (action.type) {
    case 'START_CHECK':
      return {
        ...state,
        [checkId]: {
          ...initialTaskState,
          status: 'checking',
        },
      };
    case 'CHECK_SUCCESS':
      return {
        ...state,
        [checkId]: {
          ...(state[checkId] || initialTaskState),
          status: 'success',
          error: undefined,
        },
      };
    case 'CHECK_ERROR':
      return {
        ...state,
        [checkId]: {
          ...(state[checkId] || initialTaskState),
          status: 'error',
          error: action.payload.error,
        },
      };
    case 'RETRY':
      return {
        ...state,
        [checkId]: {
          ...(state[checkId] || initialTaskState),
          status: 'checking',
          retryCount: (state[checkId]?.retryCount || 0) + 1,
          totalRetryTimeMs: (state[checkId]?.totalRetryTimeMs || 0) + action.payload.retryTime,
        },
      };
    case 'RESET':
      return {
        ...state,
        [checkId]: initialTaskState,
      };
    default:
      return state;
  }
}

/*
 * 這裡實際會遇到的錯誤形狀(axios 回應錯誤、網路錯誤、以及下方 toError 包裝過的錯誤)。
 * 欄位皆為選填, 屬性名稱打錯時可在編譯期被抓到。
 */
interface ApiError extends Error {
  status?: number;
  code?: string | number;
  response?: {
    status?: number;
    statusText?: string;
    data?: {
      message?: string;
      ErrorDescription?: string;
    };
  };
  originalError?: ApiError;
}

const toError = (error: unknown): ApiError => {
  if (error instanceof Error) return error;

  /* 非 Error 的拋出值(字串、response body 等):取出可讀訊息包成 Error,原值保留於 originalError */
  const message = typeof error === 'object' && error !== null && 'message' in error ? String((error as ApiError).message) : undefined;
  const wrappedError: ApiError = new Error(message ?? 'Unknown error');
  wrappedError.originalError = error as ApiError;
  return wrappedError;
};

// 預設的重試條件：設備重啟期間的暫時錯誤應該繼續重試
const defaultShouldRetryOnError = (error: ApiError): boolean => {
  // 檢查各種可能的錯誤格式
  const { originalError } = error;
  const errorMessage = [
    error.message,
    error.response?.statusText,
    error.response?.data?.message,
    error.response?.data?.ErrorDescription,
    originalError?.message,
    originalError?.response?.statusText,
    originalError?.response?.data?.message,
    originalError?.response?.data?.ErrorDescription,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  const status = Number(
    error.status ?? error.response?.status ?? error.code ?? originalError?.status ?? originalError?.response?.status ?? originalError?.code
  );

  return (
    [404, 502, 503, 504].includes(status) ||
    errorMessage.includes('status code 404') ||
    errorMessage.includes('404') ||
    errorMessage.includes('status code 502') ||
    errorMessage.includes('502') ||
    errorMessage.includes('bad gateway') ||
    errorMessage.includes('status code 503') ||
    errorMessage.includes('503') ||
    errorMessage.includes('service unavailable') ||
    errorMessage.includes('status code 504') ||
    errorMessage.includes('504') ||
    errorMessage.includes('gateway timeout') ||
    errorMessage.includes('not found') ||
    error.code === 'ERR_NETWORK' ||
    originalError?.code === 'ERR_NETWORK' ||
    errorMessage.includes('network error')
  );
};

export const useCheckApiReady = () => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const stateRef = useRef(state);
  stateRef.current = state;
  const timersRef = useRef<Record<string, NodeJS.Timeout>>({});
  const startTimeRef = useRef<Record<string, number>>({});
  const resolveFuncRef = useRef<Record<string, () => void>>({});

  // 清除特定 checkId 的計時器
  const clearCheckTimer = useCallback((checkId: string) => {
    if (timersRef.current[checkId]) {
      clearTimeout(timersRef.current[checkId]);
      delete timersRef.current[checkId];
    }
  }, []);

  // 重設所有計時器
  const clearAllTimers = useCallback(() => {
    Object.keys(timersRef.current).forEach((id) => {
      clearTimeout(timersRef.current[id]);
      delete timersRef.current[id];
    });
  }, []);

  // 處理狀態轉換與取得最新狀態
  const updateStartCheckState = (nextAction: Action) => {
    dispatch(nextAction);
    stateRef.current = reducer(state, nextAction);
  };

  // 基於時間的檢查
  const timeBasedCheck = useCallback(
    (
      checkId: string,
      checkFunction: () => Promise<{ data: boolean; status: number }>,
      checkResponseData: (data: any) => boolean,
      totalDuration: number,
      retryInterval: number,
      callback: Function,
      startTime: number,
      shouldRetryOnError: (error: Error) => boolean = defaultShouldRetryOnError
    ): void => {
      updateStartCheckState({ type: 'START_CHECK', payload: { checkId } });

      const checkLoop = async () => {
        // 檢查是否超過總時間
        const elapsedTime = Date.now() - startTime;
        if (elapsedTime >= totalDuration) {
          updateStartCheckState({
            type: 'CHECK_ERROR',
            payload: { checkId, error: new Error('Total duration exceeded') },
          });
          callback();
          return;
        }

        try {
          const { status, data } = await checkFunction();
          if (status === 200 && checkResponseData(data)) {
            updateStartCheckState({ type: 'CHECK_SUCCESS', payload: { checkId } });
            callback();
            return;
          }
        } catch (error) {
          const actualError = toError(error);
          console.error(`[${checkId}] Check error: ${actualError.message}`);

          const shouldContinueRetry = shouldRetryOnError(actualError);

          if (!shouldContinueRetry) {
            console.error(`[${checkId}] Non-retryable error, stopping checks`);
            updateStartCheckState({
              type: 'CHECK_ERROR',
              payload: { checkId, error: actualError },
            });
            callback();
            return;
          }

          console.log(`[${checkId}] Retryable error (${actualError.message}), will continue retrying...`);
        }

        // 計算下一次重試的時間，確保不會超過總時間
        const currentElapsedTime = Date.now() - startTime;
        const nextCheckTime = Math.min(retryInterval, totalDuration - currentElapsedTime);

        if (nextCheckTime <= 0) {
          updateStartCheckState({
            type: 'CHECK_ERROR',
            payload: { checkId, error: new Error('Total duration exceeded') },
          });
          callback();
          return;
        }

        // 記錄重試狀態
        updateStartCheckState({ type: 'RETRY', payload: { checkId, retryTime: nextCheckTime } });

        // 安排下一次重試
        timersRef.current[checkId] = setTimeout(checkLoop, nextCheckTime);
      };

      // 開始檢查循環
      checkLoop();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // 基於次數的檢查
  const countBasedCheck = useCallback(
    (
      checkId: string,
      checkFunction: () => Promise<{ data: boolean; status: number }>,
      checkResponseData: (data: any) => boolean,
      maxRetryCount: number,
      retryInterval: number,
      callback: Function,
      shouldRetryOnError: (error: Error) => boolean = defaultShouldRetryOnError
    ): void => {
      updateStartCheckState({ type: 'START_CHECK', payload: { checkId } });

      const checkLoop = async () => {
        const currentState = stateRef.current[checkId];

        // 檢查是否超過最大重試次數
        if (currentState.retryCount >= maxRetryCount) {
          updateStartCheckState({
            type: 'CHECK_ERROR',
            payload: { checkId, error: new Error('Max retry count reached') },
          });
          callback();
          return;
        }

        try {
          const { status, data } = await checkFunction();
          if (status === 200 && checkResponseData(data)) {
            updateStartCheckState({ type: 'CHECK_SUCCESS', payload: { checkId } });
            callback();
            return;
          }
        } catch (error) {
          const actualError = toError(error);
          console.error(`[${checkId}] Check error: ${actualError.message}`);

          // 使用可配置的重試條件
          const shouldContinueRetry = shouldRetryOnError(actualError);

          if (!shouldContinueRetry) {
            console.error(`[${checkId}] Non-retryable error, stopping checks`);
            updateStartCheckState({
              type: 'CHECK_ERROR',
              payload: { checkId, error: actualError },
            });
            callback();
            return;
          }

          // 如果是可重試錯誤，記錄但繼續重試
          console.log(`[${checkId}] Retryable error (${actualError.message}), will continue retrying...`);
        }

        // 重試次數達到上限
        if (currentState.retryCount + 1 >= maxRetryCount) {
          updateStartCheckState({
            type: 'CHECK_ERROR',
            payload: { checkId, error: new Error('Max retry count reached') },
          });
          callback();
          return;
        }

        // 記錄重試狀態
        updateStartCheckState({ type: 'RETRY', payload: { checkId, retryTime: retryInterval } });

        // 安排下一次重試
        timersRef.current[checkId] = setTimeout(checkLoop, retryInterval);
      };

      // 開始檢查循環
      checkLoop();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // 元件卸載時清除所有計時器
  useEffect(() => {
    return () => {
      clearAllTimers();
    };
  }, [clearAllTimers]);

  // 執行檢查流程
  const startCheck = useCallback(
    /**
     * 執行 API 檢查流程
     * @param checkId 檢查任務ID
     * @param enableRetry 是否啟用重試
     * @param retryMode 重試模式: 'time' 或 'count'
     * @param checkFunction 檢查函式，應返回 { data: boolean } 的 Promise
     * @param callbackFunction 完成時的回呼函式，接收完整的 TaskState
     * @param totalDuration 時間模式: 總執行時間(ms) | 次數模式: 總執行次數
     * @param retryInterval 重試間隔(ms)
     * @param startCheckAt 開始檢查的時間點(ms)，僅在時間模式下使用
     *
     * @example
     * // 時間模式：總執行 180 秒，在第 170 秒開始檢查，每 5 秒重試一次
     * startCheck({
     *   checkId: 'apiReady',
     *   enableRetry: true,
     *   retryMode: 'time',
     *   checkFunction: async () => { return { data: await fetchApiStatus() } },
     *   callbackFunction: (state) => console.log('Check completed with state:', state),
     *   totalDuration: 180 * 1000, // 180 秒
     *   retryInterval: 5 * 1000,   // 5 秒
     *   startCheckAt: 170 * 1000   // 170 秒
     * });
     *
     * @example
     * // 次數模式：總執行 10 次，每 3 秒重試一次
     * startCheck({
     *   checkId: 'apiReady',
     *   enableRetry: true,
     *   retryMode: 'count',
     *   checkFunction: async () => { return { data: await fetchApiStatus() } },
     *   callbackFunction: (state) => console.log('Check completed with state:', state),
     *   totalDuration: 10, // 10 次
     *   retryInterval: 3 * 1000 // 3 秒
     * });
     */
    async ({
      checkId,
      enableRetry,
      retryMode,
      checkFunction,
      checkResponseData = () => true,
      callbackFunction,
      totalDuration,
      retryInterval,
      startCheckAt = 0,
    }: UseCheckApiReadyProps): Promise<void> => {
      // 清除之前的計時器
      clearCheckTimer(checkId);

      // 重設狀態
      dispatch({ type: 'RESET', payload: { checkId } });

      return new Promise<void>((resolveCheck) => {
        const triggerCallback = () => {
          callbackFunction(stateRef.current);
          resolveCheck(); // 解析 Promise，允許 await startCheck() 繼續
        };

        // 非重試模式，直接執行一次檢查
        if (!enableRetry) {
          updateStartCheckState({ type: 'START_CHECK', payload: { checkId } });
          (async () => {
            try {
              const { status, data } = await checkFunction();
              if (status === 200 && checkResponseData(data)) {
                updateStartCheckState({ type: 'CHECK_SUCCESS', payload: { checkId } });
              } else {
                updateStartCheckState({
                  type: 'CHECK_ERROR',
                  payload: { checkId, error: new Error('API not ready') },
                });
              }
            } catch (error) {
              const actualError = toError(error);
              updateStartCheckState({
                type: 'CHECK_ERROR',
                payload: { checkId, error: actualError },
              });
            }
            triggerCallback();
          })();
          return;
        }

        // 重試模式
        if (retryMode === 'time') {
          // 時間模式參數檢查
          if (startCheckAt > totalDuration) {
            throw new Error('startCheckAt must be less than or equal to totalDuration');
          }

          // 設定開始時間
          const globalStartTime = Date.now();
          startTimeRef.current[checkId] = globalStartTime;

          // 儲存 resolve 函數以便檢查完成時使用
          resolveFuncRef.current[checkId] = triggerCallback;

          // 等待到指定的開始檢查時間點
          if (startCheckAt > 0) {
            console.log(`Will start checking in ${startCheckAt}ms`);
            timersRef.current[checkId] = setTimeout(() => {
              timeBasedCheck(checkId, checkFunction, checkResponseData, totalDuration, retryInterval, triggerCallback, globalStartTime);
            }, startCheckAt);
          } else {
            // 立即開始檢查
            timeBasedCheck(checkId, checkFunction, checkResponseData, totalDuration, retryInterval, triggerCallback, globalStartTime);
          }
        } else if (retryMode === 'count') {
          // 次數模式直接開始檢查
          resolveFuncRef.current[checkId] = triggerCallback;
          countBasedCheck(checkId, checkFunction, checkResponseData, totalDuration, retryInterval, triggerCallback);
        }
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clearCheckTimer, timeBasedCheck, countBasedCheck]
  );

  // 取消檢查
  const cancelCheck = useCallback(
    (checkId: string) => {
      clearCheckTimer(checkId);
      delete startTimeRef.current[checkId];
      dispatch({ type: 'RESET', payload: { checkId } });
    },
    [clearCheckTimer]
  );

  return {
    state,
    startCheck,
    cancelCheck,
  };
};
